import React, { useCallback, useImperativeHandle, useRef, useState } from "react";
import useApiConfiguration from "../../apiConfig";
import { GiustificativiUploadAllegatiDTO } from "../../data";
import { FileUploader, LoadPanel, Toast } from "devextreme-react";
import { FileUploaderRef } from "devextreme-react/file-uploader";
import { Popup, Position, ToolbarItem } from "devextreme-react/popup";
import { useScreenSize } from "../../utils/media-query";

export type PopupCaricaAllegatiHandle = {
    show: (rimborsi: GiustificativiUploadAllegatiDTO[]) => void
};

type Props = {
    onHide: () => void
};

type State = {
    rimborsi: GiustificativiUploadAllegatiDTO[],
    accessToken?: string,
    visible: boolean
};

const PopupCaricaAllegati: React.ForwardRefRenderFunction<PopupCaricaAllegatiHandle, Props> = (props, ref) => {
    const apiConfiguration = useApiConfiguration();
    const fileExtensions = ['.pdf', '.jpg', '.jpeg', '.gif', '.png'];
    const { isXSmall, isSmall } = useScreenSize();
    const uploaderRef = useRef<FileUploaderRef>(null);

    const [loading, setLoading] = useState<boolean>(false);
    const [toastConfig, setToastConfig] = useState({ isVisible: false, isOk: false, message: '' } as { isVisible: boolean, isOk: boolean, message: string });
    const [state, setState] = useState<State>({ rimborsi: [], accessToken: '', visible: false });
    const [fileUploaderKey, setFileUploaderKey] = useState<number>(0);

    function hideInfo() {
        setFileUploaderKey(prevKey => prevKey + 1);
        setState({ rimborsi: [], visible: false });

        props.onHide();
    };

    useImperativeHandle(ref, () => ({
        async show(rimborsi: GiustificativiUploadAllegatiDTO[]) {
            setLoading(true);

            let token: string | undefined;
            if (typeof apiConfiguration.accessToken === 'function')
                token = await apiConfiguration.accessToken();
            else
                token = apiConfiguration.accessToken;

            setState({ rimborsi: rimborsi, visible: true, accessToken: token });

            setLoading(false);
        }
    }));

    const onUpload = async () => {
        setLoading(true);

        const files = uploaderRef?.current?.instance().option("value") as File[];
        if (!files.length) {
            setToastConfig({
                isVisible: true,
                isOk: false,
                message: "Allegare almeno un file"
            });
            return;
        }

        const formData = new FormData();
        files.forEach(file => { formData.append("files", file); });
        state.rimborsi.forEach((r, index) => {
            formData.append(`rimborsi[${index}].idLavoro`, r.idLavoro.toString());
            formData.append(`rimborsi[${index}].idTipoRimborso`, r.idTipoRimborso.toString());
        });

        try {
            const response = await fetch(`${apiConfiguration.basePath}/api/Giustificativi/UploadAllegati`, {
                headers: {
                    'Authorization': state.accessToken
                },
                body: formData,
                method: 'POST'
            });

            if (response.ok) {
                setToastConfig({
                    isVisible: true,
                    isOk: true,
                    message: 'File caricato/i con successo',
                });

                hideInfo();
            } else {
                console.error(response);

                setToastConfig({
                    isVisible: true,
                    isOk: false,
                    message: 'Errore durante l\'upload del file',
                });
            }
        } catch (error) {
            console.error(error);

            setToastConfig({
                isVisible: true,
                isOk: false,
                message: 'Errore durante l\'upload del file',
            });
        } finally {
            setLoading(false);
        }
    };

    const onHidingToast = useCallback(() => {
        setToastConfig({
            ...toastConfig,
            isVisible: false
        });
    }, [toastConfig, setToastConfig]);

    return (
        <React.Fragment>
            <Popup
                visible={state.visible} showTitle showCloseButton
                dragEnabled={false} fullScreen={(isXSmall || isSmall)}
                hideOnOutsideClick onHidden={hideInfo} title="Carica Allegati"
                width={600} height={425}
            >
                {loading && <LoadPanel style={{ zIndex: 9999 }} position={'center'} visible shading shadingColor='rgba(0, 0, 0, 0.4)' />}

                <Position at="center" my="center" of={window} collision="fit" />

                <div style={{ padding: '20px' }}>
                    <FileUploader
                        ref={uploaderRef} key={fileUploaderKey}
                        multiple uploadMode="useForm"
                        allowedFileExtensions={fileExtensions}
                    />
                </div>

                <ToolbarItem
                    widget="dxButton"
                    toolbar="bottom"
                    location="center"
                    options={{
                        text: "Carica",
                        type: "default",
                        stylingMode: "contained",
                        onClick: onUpload
                    }}
                />

                <ToolbarItem
                    widget="dxButton"
                    toolbar="bottom"
                    location="center"
                    options={{
                        text: "Chiudi",
                        stylingMode: "outlined",
                        onClick: hideInfo
                    }}
                />
            </Popup>

            <Toast
                visible={toastConfig.isVisible}
                message={toastConfig.message}
                height={45} width={200}
                position={{ at: 'right bottom', my: 'right bottom', of: window, offset: { x: -20, y: -20 } }}
                type={toastConfig.isOk ? 'success' : 'error'}
                onHiding={onHidingToast} closeOnClick
                displayTime={3000}
                animation={{ show: { type: 'fade', duration: 400, from: 0, to: 1 }, hide: { type: 'fade', duration: 40, to: 0 } }}
            />
        </React.Fragment>
    );
};

export default React.forwardRef(PopupCaricaAllegati);